import clsx from 'clsx';

import type * as api from '@gitbook/api-client';
import type { SiteSpace } from '@gitbook/api-client';
import { Picker, type PickerItemData } from '@gitbook/react-uikit';
import { RotateLeft } from '@gitbook/spine-icons/src/icons';
import { Button, confirm, Description, FieldGroup, toast } from '@gitbook/spine-react';
import { useEventCallback } from '@gitbook/util-react-hooks';

import { apiClient } from '../../state';
import type { SitesCustomizationPanelProps } from './types';

/**
 * Section for picking a variant in Customization modal
 */
export function CustomizationSiteSpacePicker({
    siteSpaceId,
    setCustomizationSiteSpace,
    siteSpacesPickerItems,
    organizationId,
    site,
    settings,
}: {
    siteSpaceId?: SiteSpace['id'];
    setCustomizationSiteSpace: (siteSpaceId: SiteSpace['id'] | undefined) => void;
    siteSpacesPickerItems: PickerItemData[];
} & SitesCustomizationPanelProps) {
    const hasOverrides = 'hasOverrides' in settings ? settings.hasOverrides : false;

    return (
        <FieldGroup className="rounded border p-4">
            <Description>Pick a section or variant to override its customization.</Description>
            <div className={clsx('grid', hasOverrides && 'grid-cols-[1fr_auto] gap-2')}>
                <Picker
                    value={siteSpaceId}
                    items={siteSpacesPickerItems}
                    onValueChange={(value) => {
                        // Because pickers need a `value` that isnt `undefined`, and we want to rely on `undefined` for detecting which kind of customization to show, we need to convert the value to `undefined`. The picker options is defined in useSiteCustomization.tsx at `siteSpacesPickerItems`
                        setCustomizationSiteSpace(value === 'site' ? undefined : value);
                    }}
                />
                {siteSpaceId && hasOverrides ? (
                    <ResetSiteSpaceCustomization
                        organizationId={organizationId}
                        site={site}
                        siteSpaceId={siteSpaceId}
                    />
                ) : null}
            </div>
        </FieldGroup>
    );
}

/**
 */
function ResetSiteSpaceCustomization(props: {
    organizationId: string;
    site: api.Site;
    siteSpaceId: string;
}) {
    const { organizationId, site, siteSpaceId } = props;

    const onResetSiteSpaceCustomization = useEventCallback(() => {
        confirm({
            kind: 'danger',
            title: 'Reset customization',
            description: (
                <>
                    Remove all space-level customizations. The space will inherit the site-wide
                    settings instead. Any customizations made to this space will be lost.
                </>
            ),

            confirmLabel: 'Reset',
            onConfirm: () =>
                toast.catch(async () => {
                    await apiClient.siteCustomization.deleteSiteSpaceCustomizationById(
                        organizationId,
                        site.id,
                        siteSpaceId
                    );

                    toast.success('Successfully reset space customization');
                }, 'Error while resetting space customization'),
        });
    });

    return (
        <Button
            label="Reset space customization to default"
            leadingIcon={<RotateLeft />}
            iconOnly={true}
            size="medium"
            kind="secondary"
            onClick={onResetSiteSpaceCustomization}
        />
    );
}
